import { useRef } from 'react';
import { MenuItem } from 'react-rainbow-components';
import {
    hideAppSpinner, showAppNotification, showAppSpinner,
} from '@rainbow-modules/app';
import {
    Ban, CheckCircle2, PenLine, Printer, Send,
} from 'lucide-react';
import ButtonIcon from 'components/ButtonIcon';
import ArrowsDiagonal from 'components/icons/arrowsDiagonal';
import Download from 'components/icons/download';
import DotsVertical from 'components/icons/dotsVertical';
import Pencil from 'components/icons/pencil2';
import SmsIcon from 'components/icons/sms';
import useTwilioConversation from 'hooks/useTwilioConversation';
import RequestSignatureButton, { RequestSignatureButtonHandle } from '../../RequestSignatureButton';
import useInvoiceCustomerPhone from '../../useInvoiceCustomerPhone';
import useSendInvoiceBySms from '../../useSendInvoiceBySms';
import { fetchInvoiceForOutput } from '../../api/client';
import {
    downloadPrestigeSticker,
    renderPrestigeStickerPdf,
} from '../../downloadInvoice';
import { printPdfBlob } from '../../printInvoice';
import { isPrestigeInvoiceAgent } from '../../InvoicePdf/prestige';
import { InvoiceRow, InvoiceSignatureState } from '../../types';
import { ActionsCell, InvoiceActionsMenu, VoidMenuItem } from '../../styled';

interface ActionsColumnProps {
    row?: InvoiceRow & {
        signatureState?: InvoiceSignatureState;
        canEdit?: boolean;
    };
    organizationId: string;
    onView?: (invoice: InvoiceRow) => void;
    onEdit?: (invoice: InvoiceRow) => void;
    onPrint?: (invoice: InvoiceRow) => void;
    onDownload?: (invoice: InvoiceRow) => void;
    onMarkSent?: (invoice: InvoiceRow) => void;
    onMarkPaid?: (invoice: InvoiceRow) => void;
    onVoid?: (invoice: InvoiceRow) => void;
}

const ActionsColumn = ({
    row,
    organizationId,
    onView,
    onEdit,
    onPrint,
    onDownload,
    onMarkSent,
    onMarkPaid,
    onVoid,
}: ActionsColumnProps) => {
    const signatureRef = useRef<RequestSignatureButtonHandle>(null);
    const { isSMSEnabled } = useTwilioConversation();
    const sendInvoiceBySms = useSendInvoiceBySms(organizationId);
    const {
        customer,
        phone,
        hasPhone,
        isPending,
    } = useInvoiceCustomerPhone(row?.customer.id);
    if (!row) return null;
    const canSendSms = isSMSEnabled && row.status !== 'void';
    const awaitingSignature = row.signatureState === 'awaiting'
        || row.signatureState === 'unrequested';
    const canMarkSent = row.status === 'draft';
    const canMarkPaid = row.status === 'draft' || row.status === 'sent' || row.status === 'overdue';
    const canVoid = row.status !== 'void';
    const showSticker = isPrestigeInvoiceAgent(organizationId);

    const runStickerAction = async (action: 'print' | 'download') => {
        showAppSpinner();
        try {
            const result = await fetchInvoiceForOutput(organizationId, row.id);
            if (action === 'download') {
                await downloadPrestigeSticker(result.invoice, result.acceptance, organizationId);
            } else {
                printPdfBlob(
                    await renderPrestigeStickerPdf(
                        result.invoice,
                        result.acceptance,
                        organizationId,
                    ),
                    'Print sticker',
                );
            }
        } catch (error: any) {
            showAppNotification({
                title: action === 'download' ? 'Could not download sticker' : 'Could not print sticker',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    };

    return (
        <ActionsCell>
            <ButtonIcon
                size="small"
                borderRadius="semi-rounded"
                icon={<ArrowsDiagonal />}
                tooltip="View invoice"
                onClick={() => onView?.(row)}
            />
            <InvoiceActionsMenu
                menuAlignment="right"
                menuSize="small"
                buttonVariant="base"
                buttonSize="small"
                borderRadius="semi-rounded"
                icon={<DotsVertical />}
                title="More actions"
            >
                {canSendSms ? (
                    <MenuItem
                        label="Send by SMS"
                        icon={<SmsIcon />}
                        disabled={!hasPhone}
                        title={isPending || hasPhone ? undefined : 'This customer has no phone number'}
                        onClick={() => sendInvoiceBySms({
                            invoiceId: row.id,
                            customer,
                            phone,
                        })}
                    />
                ) : null}
                {showSticker ? (
                    <>
                        <MenuItem
                            label="Print"
                            icon={<Printer size={16} />}
                            onClick={() => onPrint?.(row)}
                        />
                        <MenuItem
                            label="Download"
                            icon={<Download />}
                            onClick={() => onDownload?.(row)}
                        />
                        <MenuItem
                            label="Print sticker"
                            icon={<Printer size={16} />}
                            onClick={() => runStickerAction('print')}
                        />
                        <MenuItem
                            label="Download sticker"
                            icon={<Download />}
                            onClick={() => runStickerAction('download')}
                        />
                    </>
                ) : (
                    <>
                        <MenuItem
                            label="Print"
                            icon={<Printer size={16} />}
                            onClick={() => onPrint?.(row)}
                        />
                        <MenuItem
                            label="Download"
                            icon={<Download />}
                            onClick={() => onDownload?.(row)}
                        />
                    </>
                )}
                {row.canEdit ? (
                    <MenuItem
                        label="Edit"
                        icon={<Pencil />}
                        onClick={() => onEdit?.(row)}
                    />
                ) : null}
                {awaitingSignature ? (
                    <MenuItem
                        label="Create signature link"
                        icon={<PenLine size={16} />}
                        onClick={() => signatureRef.current?.open()}
                    />
                ) : null}
                {canMarkSent ? (
                    <MenuItem
                        label="Mark as sent"
                        icon={<Send size={16} />}
                        onClick={() => onMarkSent?.(row)}
                    />
                ) : null}
                {canMarkPaid ? (
                    <MenuItem
                        label="Mark as paid"
                        icon={<CheckCircle2 size={16} />}
                        onClick={() => onMarkPaid?.(row)}
                    />
                ) : null}
                {canVoid ? (
                    <VoidMenuItem
                        label="Void invoice"
                        icon={<Ban size={16} />}
                        onClick={() => onVoid?.(row)}
                    />
                ) : null}
            </InvoiceActionsMenu>
            {awaitingSignature ? (
                <RequestSignatureButton
                    ref={signatureRef}
                    showTrigger={false}
                    organizationId={organizationId}
                    invoiceId={row.id}
                />
            ) : null}
        </ActionsCell>
    );
};

export default ActionsColumn;
